import { Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';

function EquipmentStatsChart({
  estatisticasEquipamentos = [],
  clientesAtendidos = [],
  isLoading,
}) {
  const isProd = process.env.NODE_ENV === 'production';
  const debugLog = (...args) => {
    if (!isProd) console.log('[EquipmentStatsChart]', ...args);
  };
  // Mapeia as possíveis chaves do nome do equipamento
  const getEquipmentName = (item) =>
    item?.equipamento ||
    item?.nomeEquipamento ||
    item?.equipment ||
    item?.device ||
    item?.modelo ||
    item?.model ||
    item?.nome ||
    item?.name ||
    'Desconhecido';

  // Obtém e valida a data do item
  const parseBrazilianDate = (str) => {
    // Suporta formatos DD/MM/AAAA ou DD/MM/AAAA HH:mm:ss
    const m = String(str).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (!m) return null;
    const [, dd, mm, yyyy, HH = '0', MM = '0', SS = '0'] = m;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(HH), Number(MM), Number(SS));
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const getDate = (item) => {
    // Checa campos numéricos tipo timestamp (segundos ou milissegundos)
    const ts = item?.timestamp || item?.time || item?.createdAt?.seconds;
    if (typeof ts === 'number') {
      const ms = ts > 9999999999 ? ts : ts * 1000;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const raw = item?.dataAtendimento || item?.createdAt || item?.data || item?.date;
    if (!raw) return null;

    if (raw instanceof Date) return raw;
    if (typeof raw === 'object' && typeof raw?.seconds === 'number') {
      const d = new Date(raw.seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // Tenta ISO/UTC
    const iso = new Date(raw);
    if (!Number.isNaN(iso.getTime())) return iso;

    // Tenta dd/mm/aaaa
    const br = parseBrazilianDate(raw);
    if (br) return br;

    return null;
  };

  // Mês/ano atual
  const now = new Date();
  const currentMonth = now.getMonth(); // 0..11
  const currentYear = now.getFullYear();
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const monthLabel = `${monthNames[currentMonth]}/${currentYear}`;
  let estatisticasEquipamentosLen = 0;
  if (Array.isArray(estatisticasEquipamentos)) {
    estatisticasEquipamentosLen = estatisticasEquipamentos.length;
  } else if (estatisticasEquipamentos) {
    estatisticasEquipamentosLen = 'obj';
  }
  const clientesAtendidosLen = Array.isArray(clientesAtendidos) ? clientesAtendidos.length : 0;
  debugLog('Iniciando', {
    currentMonth,
    currentYear,
    monthLabel,
    estatisticasEquipamentos_len: estatisticasEquipamentosLen,
    clientesAtendidos_len: clientesAtendidosLen,
  });

  // Normaliza a fonte de dados (aceita array ou objetos com listas internas)
  const items = Array.isArray(estatisticasEquipamentos)
    ? estatisticasEquipamentos
    : (estatisticasEquipamentos?.itens ||
       estatisticasEquipamentos?.items ||
       estatisticasEquipamentos?.equipamentos ||
       estatisticasEquipamentos?.dados ||
       estatisticasEquipamentos?.data ||
       []);
  debugLog('Itens normalizados', { quantidade: Array.isArray(items) ? items.length : 0 });

  // Filtra para o mês/ano atual e agrupa por equipamento
  let statTotal = 0;
  let statSemData = 0;
  let statForaDoMes = 0;
  let statUsouMesAnoSeparado = 0;
  let statIncluidos = 0;
  const amostrasIncluidas = [];
  const amostrasExcluidas = [];
  const countsByEquipment = items.reduce((acc, item) => {
    statTotal += 1;
    // Alguns payloads trazem mes/ano já separados (1-12)
    const monthField = item?.mes ?? item?.month ?? item?.mesReferencia;
    const yearField = item?.ano ?? item?.year ?? item?.anoReferencia;

    if (monthField != null && yearField != null) {
      const y = Number(yearField);
      const mRaw = Number(monthField);
      let m = null;
      if (!Number.isNaN(mRaw)) {
        if (mRaw >= 0 && mRaw <= 11) m = mRaw; // já é 0..11
        else if (mRaw >= 1 && mRaw <= 12) m = mRaw - 1; // 1..12
      }
      statUsouMesAnoSeparado += 1;
      if (m === null || y !== currentYear || m !== currentMonth) {
        statForaDoMes += 1;
        if (amostrasExcluidas.length < 5) amostrasExcluidas.push({ motivo: 'mes/ano separado fora do mes', item });
        return acc;
      }
    } else {
      const d = getDate(item);
      if (!d) {
        statSemData += 1;
        if (amostrasExcluidas.length < 5) amostrasExcluidas.push({ motivo: 'sem data', item });
        return acc;
      }
      if (d.getMonth() !== currentMonth || d.getFullYear() !== currentYear) {
        statForaDoMes += 1;
        if (amostrasExcluidas.length < 5) amostrasExcluidas.push({ motivo: 'fora do mes', data: d?.toISOString?.(), item });
        return acc;
      }
    }

    const d = getDate(item);
    // d pode ser null quando filtrado por mes/ano explícitos; tudo bem

    const key = getEquipmentName(item);
    const inc = typeof item?.count === 'number' ? item.count : 1;
    acc[key] = (acc[key] || 0) + inc;
    statIncluidos += 1;
    if (amostrasIncluidas.length < 5) amostrasIncluidas.push({ key, inc, data: d?.toISOString?.() });
    return acc;
  }, {});

  // Fallback: se não houver dados, tenta construir a partir de clientesAtendidos
  let effectiveCounts = countsByEquipment;
  debugLog('Resumo (fonte principal)', {
    monthLabel,
    statTotal,
    statSemData,
    statForaDoMes,
    statUsouMesAnoSeparado,
    statIncluidos,
    labels: Object.keys(countsByEquipment),
    valores: Object.values(countsByEquipment),
    amostrasIncluidas,
    amostrasExcluidas,
  });

  if (Object.keys(effectiveCounts).length === 0 && Array.isArray(clientesAtendidos)) {
    let fbTotal = 0;
    let fbIncluidos = 0;
    let fbForaDoMes = 0;
    const fbInclSamples = [];
    const fbExclSamples = [];
    effectiveCounts = clientesAtendidos.reduce((acc, item) => {
      fbTotal += 1;
      // Usa dataFim para o término do atendimento
      const d = getDate({
        dataAtendimento: item?.dataFim || item?.fimAtendimento || item?.data,
      });
      if (!d) {
        if (fbExclSamples.length < 5) fbExclSamples.push({ motivo: 'fb sem data', item });
        return acc;
      }
      if (d.getMonth() !== currentMonth || d.getFullYear() !== currentYear) {
        fbForaDoMes += 1;
        if (fbExclSamples.length < 5) fbExclSamples.push({ motivo: 'fb fora do mes', data: d?.toISOString?.(), item });
        return acc;
      }
      const key =
        item?.tipoEquipamento ||
        item?.equipamento ||
        item?.nomeEquipamento ||
        'Desconhecido';
      acc[key] = (acc[key] || 0) + 1;
      fbIncluidos += 1;
      if (fbInclSamples.length < 5) fbInclSamples.push({ key, data: d?.toISOString?.() });
      return acc;
    }, {});
    debugLog('Fallback clientesAtendidos', {
      monthLabel,
      fbTotal,
      fbForaDoMes,
      fbIncluidos,
      labels: Object.keys(effectiveCounts),
      valores: Object.values(effectiveCounts),
      fbInclSamples,
      fbExclSamples,
    });
  }

  const labels = Object.keys(effectiveCounts);
  const dataValues = labels.map((k) => effectiveCounts[k]);
  const hasData = dataValues.some((v) => v > 0);

  // Paleta de cores (repete conforme necessário)
  const palette = [
    '#635BFF',
    '#FF6384',
    '#36A2EB',
    '#FFCE56',
    '#4BC0C0',
    '#9966FF',
    '#FF9F40',
    '#8D6E63',
    '#26A69A',
    '#7CB342',
    '#AB47BC',
    '#5C6BC0',
    '#F06292',
  ];
  const backgroundColor = hasData
    ? labels.map((_, i) => palette[i % palette.length])
    : ['#2d3748'];

  const chartData = {
    labels: hasData ? labels : ['Sem dados 😴'],
    datasets: [
      {
        data: hasData ? dataValues : [1],
        backgroundColor,
        borderColor: '#1e293b',
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: { display: true, position: 'bottom', labels: { color: '#cbd5e1' } },
      tooltip: { enabled: hasData },
    },
    cutout: '70%',
    maintainAspectRatio: false,
  };

  debugLog('Pronto para renderizar', { labels, dataValues, hasData });

  let chartContent;
  if (isLoading) {
    chartContent = (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else {
    chartContent = (
      <>
        <Doughnut data={chartData} options={chartOptions} />
        {!hasData && (
          <Typography
            variant="body2"
            sx={{
              color: '#8F99A8',
              mt: 2,
              textAlign: 'center',
              fontStyle: 'italic',
            }}
          >
            Ainda não há atendimentos para o mês de {monthLabel}.
          </Typography>
        )}
      </>
    );
  }

  return (
    <Card sx={{ height: '100%', backgroundColor: '#1e293b', color: 'white' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Atendimentos por Equipamento ({monthLabel})
        </Typography>
        <Box sx={{ height: 365, position: 'relative' }}>{chartContent}</Box>
      </CardContent>
    </Card>
  );
}

export default EquipmentStatsChart;
